/**
 * Auto-sync Notion status when viewing an issue that has a Notion export.
 *
 * Fires once per page load. If the Notion page's status/severity/assignee
 * changed, the sync endpoint updates the OverlayQA issue and this hook
 * invalidates the query cache so the UI reflects the new state.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import type { Ticket } from '@overlayqa/shared';
import { fetchApi } from '../lib/api/client';
import { log } from '@/lib/logger';

export function useNotionAutoSync(ticket: Ticket | undefined) {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();
  const syncedRef = useRef<string | null>(null);

  useEffect(() => {
    // Array.isArray also rejects malformed jsonb (e.g. `{}`) — a corrupt row
    // must not crash the detail page (2026-07-14).
    if (!ticket?.id || !Array.isArray(ticket.exportedTo)) return;

    // Only sync once per ticket load
    if (syncedRef.current === ticket.id) return;

    const notionExport = ticket.exportedTo.find((e) => e.provider === 'notion');
    if (!notionExport) return;

    syncedRef.current = ticket.id;

    (async () => {
      try {
        const token = await getToken();
        const result = await fetchApi<{
          synced: boolean;
          changes?: string[];
          notionStatus?: string;
        }>(`/integrations/notion/sync/${ticket.id}`, {
          token: token ?? undefined,
          method: 'POST',
        });

        if (result.synced) {
          log.info('[useNotionAutoSync] Changes synced from Notion', {
            ticketId: ticket.id,
            changes: result.changes,
          });
          // Invalidate the issue query so the UI refreshes with updated data
          queryClient.invalidateQueries({ queryKey: ['issue', ticket.id] });
          queryClient.invalidateQueries({ queryKey: ['tickets'] });
        }
      } catch (err) {
        // Non-fatal: if Notion sync fails (not connected, token expired, etc.),
        // the issue still loads with its current state
        log.info('[useNotionAutoSync] Sync failed (non-fatal)', {
          ticketId: ticket.id,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    })();
  // Only depend on ticket.id (not exportedTo array reference, which changes on every re-render)
  // The syncedRef guard prevents duplicate calls per ticket.id
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id, getToken, queryClient]);
}
